import { getMarkStatus, isPasswordProtected, regExpEscape, toMoment, trimStart } from "@/services/UtilitiesService";
import { Metadata } from "@/models/Metadata";
import { SortBy, SortOrder } from "@/models/Filter";
import moment from "moment";
import { metadataService } from "@/services";
import { ModuleStatus } from "@/models/ModuleStatus";
import { defineStore } from "pinia";
import { useFiltersStore } from "@/stores/filters-store";
import { useEvidenceCountStore } from "@/stores/evidence-count-store";
import { useIoaStore } from "@/stores/ioa-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useBrandStore } from "@/stores/brand-store";
import { useNotesStore } from "@/stores/notes-store";
import { useUserSessionStore } from "@/stores/user-session-store";
import { useTagStore } from "@/stores/tags-store";
import { useCaseStore } from "./case-store";
import { useCaseFileStore } from "./case-file-store";

interface State {
  files: readonly Metadata[];
  failedFiles: readonly Metadata[];
  moduleStatus: ModuleStatus;
}
interface Counts {
  draftFileCount: number;
  totalFileCount: number;
  passwordProtectedCount: number;
  unknownFileCount: number;
}
export const useMetadataStore = defineStore("metadata", {
  scope: "case",
  state: (): State => ({
    files: [],
    failedFiles: [],
    moduleStatus: ModuleStatus.Initial,
  }),
  getters: {
    counts(): Counts {
      const filesByRef = useMetadataStore().filesByRef;
      return this.files.reduce(
        (a, f) => {
          const aliasesLength = f.aliases ? f.aliases.length : 0;
          return {
            draftFileCount: a.draftFileCount + (String(f.isDraft).toLowerCase() === "true" ? aliasesLength + 1 : 0),
            totalFileCount:
              a.totalFileCount +
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              (f.fileType !== "blacklist" && filesByRef[f.parent || ""]?.fileType !== "ac5" ? aliasesLength + 1 : 0),
            passwordProtectedCount: a.passwordProtectedCount + (isPasswordProtected(f) ? aliasesLength + 1 : 0),
            unknownFileCount: a.unknownFileCount + (f.fileReference === "unknown" ? aliasesLength + 1 : 0),
          };
        },
        {
          draftFileCount: 0,
          totalFileCount: 0,
          passwordProtectedCount: 0,
          unknownFileCount: 0,
        },
      );
    },
    allFilesUnsorted(): readonly Metadata[] {
      const tagsObject = useTagStore().tagsObject;
      const notesObject = useNotesStore().notesObject;
      const evidenceCounts = useEvidenceCountStore().evidenceCounts;

      const evidenceCountsByFileReference = new Map<string, Array<{ name: string; value: number }>>();
      for (const evidenceCount of evidenceCounts)
        evidenceCountsByFileReference.set(evidenceCount.fileReference, evidenceCount.counts);

      const spreadFiles: readonly Metadata[] = this.files.map((f) => {
        const notes = notesObject[f.fileReference]?.length ?? 0;
        const allTags = tagsObject[f.fileReference] ?? [];
        const count = evidenceCountsByFileReference.get(f.original) ?? [];

        return {
          ...f,
          notes,
          allTags,
          evidenceCounts: count,
          tags: allTags.filter((t) => t.tagRestriction !== "autoTag"),
          autoTags: allTags.filter((t) => t.tagRestriction === "autoTag"),
        };
      });

      const edxFilenameByFileReference = new Map<string, string>();
      for (const file of spreadFiles) {
        edxFilenameByFileReference.set(file.fileReference, file.edxFilename);

        for (const alias of file.aliases ?? [])
          edxFilenameByFileReference.set(alias.fileReference, alias.metadata.edxFilename);
      }

      const dupeFiles: readonly Metadata[] = Object.freeze(
        spreadFiles.flatMap((f) => {
          if (f.aliases == null || f.aliases.length < 1) return [];

          return f.aliases.map((m) => {
            const item = {
              ...f,
              ...m.metadata,
              fileReference: m.fileReference,
              parent: m.parent,
              isDuplicate: true,
              original: f.fileReference,
              edxFilename: m.metadata.edxFilename,
              clnFileName: trimStart(m.metadata.edxFilename, "/"),
            };

            if (item.parent && item.parent.length > 0) {
              const parentEdxFilename = edxFilenameByFileReference.get(item.parent);

              if (parentEdxFilename) {
                item.clnFileName = trimStart(item.edxFilename, "/").replace(
                  new RegExp(`^/?${regExpEscape(parentEdxFilename)}/?`),
                  "",
                );
              }
            }

            return item;
          });
        }),
      );

      return spreadFiles.concat(dupeFiles);
    },
    requestedFiles(): readonly Metadata[] {
      const filter = useFiltersStore().filter;

      const sort = useUserSessionStore().brandSort;
      const defaultSort = sort.sortBy === SortBy.PAGE_COUNT ? "0" : "";

      const files = this.allFilesUnsorted.filter(
        (f) => f.fileType !== "blacklist" && this.filesByRef[f.parent ?? ""]?.fileType !== "ac5",
      );

      function sortFiles(a: Metadata, b: Metadata) {
        let fieldA = String(a[sort.sortBy] ?? defaultSort);
        let fieldB = String(b[sort.sortBy] ?? defaultSort);

        let comparison = fieldA.localeCompare(fieldB, undefined, { numeric: true, sensitivity: "base" });
        if (comparison !== 0) return sort.sortOrder === SortOrder.ASC ? comparison : comparison * -1;

        // To keep files in a reasonable order, sort by displayName desc before fileReference sorting
        fieldA = a.displayName || "";
        fieldB = b.displayName || "";

        comparison = fieldA.localeCompare(fieldB, undefined, { numeric: true, sensitivity: "base" });
        if (comparison !== 0) return sort.sortOrder === SortOrder.ASC ? comparison : comparison * -1;

        // To prevent shuffling, filter by something unique
        // File reference should be unique, filter by that next
        fieldA = a.fileReference;
        fieldB = b.fileReference;

        comparison = fieldA.localeCompare(fieldB, undefined, { numeric: true, sensitivity: "base" });
        if (comparison !== 0) return sort.sortOrder === SortOrder.ASC ? comparison : comparison * -1;

        return 0;
      }

      if (filter.hideDuplicates) {
        return files.filter((file) => file.isDuplicate !== true).sort(sortFiles);
      }

      return files.sort(sortFiles);
    },
    filteredFiles(): readonly Metadata[] {
      return getFilteredFiles(this.requestedFiles, false);
    },
    filteredFilesInCurrentFolder(): readonly Metadata[] {
      return getFilteredFiles(this.requestedFiles, true);
    },
    filesByRef(): Readonly<Record<string, Metadata>> {
      return Object.freeze(
        // This doesn't need to use the sorted version
        this.allFilesUnsorted.reduce((prev, curr) => Object.assign(prev, { [curr.fileReference]: curr }), {}),
      );
    },
    hierarchy(): string[] {
      if (!this.requestedFiles) {
        return [];
      }

      const filenames = this.requestedFiles.map((m) => `/${m.clnFileName}`);
      const tree: string[] = [];

      filenames.forEach((entry) => {
        const pathParts = entry.split("/");
        pathParts.shift(); // Remove first blank element from the parts array.
        pathParts.pop(); // Get rid of the file

        for (let pathIndex = 0; pathIndex < pathParts.length; pathIndex++) {
          tree.push(pathParts.slice(0, pathIndex + 1).join("/"));
        }
      });

      return [...new Set(tree)];
    },
    folders() {
      const currentFolder = useFiltersStore().filter.currentFolder;
      const folderDepth = currentFolder ? currentFolder.split("/").length : 0;
      const folders: string[] = this.hierarchy
        .filter((h) => h.includes(currentFolder))
        .map((h) => h.split("/"))
        .filter((h) => h.length > folderDepth)
        .map((h) => h[folderDepth]);
      return [...new Set(folders)];
    },
  },
  actions: {
    async load(): Promise<void> {
      const caseReference = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || caseReference == null) return;

      this.setMetadataStatus(ModuleStatus.Loading);
      try {
        const metadata: Metadata[] = await metadataService.getAll(selectedClient, selectedBrand, caseReference);
        this.loaded(metadata);
        this.setMetadataStatus(ModuleStatus.Ready);
      } catch (error) {
        console.log(error);
        this.setMetadataStatus(ModuleStatus.Error);
      }
    },
    async refresh(): Promise<void> {
      const caseReference = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || caseReference == null) return;

      if (this.moduleStatus === ModuleStatus.Refreshing) return;

      this.setMetadataStatus(ModuleStatus.Refreshing);

      try {
        const metadata: Metadata[] = await metadataService.refresh(selectedClient, selectedBrand, caseReference);
        this.loaded(metadata);
      } finally {
        this.setMetadataStatus(ModuleStatus.Ready);
      }
    },
    passwordStatusPending() {
      this.triggerPolling();
    },
    async updateMetadata(payload: { property: string; value: unknown }): Promise<void> {
      const item: { [x: string]: string } = {};

      const { selectedBrand, selectedClient } = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      const selectedCaseFile = useCaseFileStore().selectedCaseFile;
      if (selectedBrand == null || selectedClient == null || selectedCaseName == null || selectedCaseFile == null)
        return;

      if (payload.property === "aiClassification") {
        await metadataService.saveClassification(selectedClient, selectedBrand, selectedCaseName, selectedCaseFile, {
          classification: payload.value,
          source: "user",
        });
      } else {
        await metadataService.saveProperty(
          selectedClient,
          selectedBrand,
          selectedCaseName,
          selectedCaseFile,
          payload.property,
          item,
        );
      }

      item[payload.property] = payload.value as string;

      await this.refresh();
      this.triggerPolling();
    },

    // Mutations
    setMetadataStatus(payload: ModuleStatus): void {
      this.moduleStatus = payload;
    },
    triggerPolling() {
      // Do nothing. Placeholder to reset polling plugin tier
    },
    loaded(payload: Metadata[]): void {
      const getFileFolder = (f: Metadata) => (f.clnFileName || f.edxFilename).split("/").slice(0, -1).join("/");

      // edxFilename contains the parent container filename, we should remove this
      const transformed = payload.reduce((acc, m, _, arr) => {
        try {
          m.fileType = m.fileType.toLowerCase();
          m.original = m.fileReference;
          m.page = undefined;
          m.annotation = undefined;
          m.time = undefined;

          if (m.parent && m.parent.length > 0) {
            const parent = arr.find((f) => f.fileReference === m.parent);
            if (parent !== undefined) {
              m.clnFileName = trimStart(m.edxFilename, "/").replace(
                new RegExp(`^/?${regExpEscape(parent.edxFilename)}/?`),
                "",
              );
            }
          } else {
            m.clnFileName = trimStart(m.edxFilename, "/");
          }

          switch (m.relation) {
            case "EmailAttachment": {
              const parent = arr.find((d) => d.fileReference === m.parent);
              if (parent != null) m.folder = getFileFolder(parent);
              break;
            }
            default:
              m.folder = getFileFolder(m);
              break;
          }

          const passed = acc.get("passed") ?? [];
          if (passed.length < 1) acc.set("passed", passed);

          passed.push(m);
        } catch (e) {
          console.error(e);

          const failed = acc.get("failed") ?? [];
          if (failed.length < 1) acc.set("failed", failed);
          failed.push(m);
        }

        return acc;
      }, new Map<"passed" | "failed", Metadata[]>());

      this.files = Object.freeze(transformed.get("passed") ?? []);
      this.failedFiles = Object.freeze(
        (transformed.get("failed") ?? []).filter((f) => f.isDuplicate !== true && f.parent == null),
      );
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.files = [];
    },
  },
});

const NO_INDICATORS = Object.freeze([{ name: "No Indicators", value: 1 }]);

function getFilteredFiles(requestedFiles: readonly Metadata[], checkFolder: boolean): readonly Metadata[] {
  const filter = useFiltersStore().filter;
  const annotationStore = useAnnotationStore();

  const dateRangeFrom = filter.dateRange.from !== "" ? toMoment(filter.dateRange.from, true) : undefined;
  const dateRangeTo = filter.dateRange.to !== "" ? toMoment(filter.dateRange.to, false) : undefined;
  const dateRangeUpper = dateRangeTo ?? dateRangeFrom ?? moment();

  const classificationFilters = new Set(filter.classifications);
  const summariserStatusFilters = new Set(filter.summariserStatuses);
  const evidenceCountFilters = new Set(filter.evidenceCounts.map((name) => name.toLowerCase()));
  const adviceInstance =
    filter.adviceInstanceId === ""
      ? undefined
      : useIoaStore().instanceOfAdvices.find((instance) => instance.instanceReference === filter.adviceInstanceId);
  const adviceInstanceFilters = adviceInstance == null ? undefined : new Set(adviceInstance.fileReferences);

  const tagFilters = new Map<string, { includeBlanks: boolean; tags: Set<string> }>();
  for (const tagFilter of filter.tags) {
    if (tagFilter.tags.length === 0) continue;
    tagFilters.set(tagFilter.tagType, { includeBlanks: tagFilter.includeBlanks, tags: new Set(tagFilter.tags) });
  }

  const agreementPeriodFrom =
    filter.agreementPeriod == null ? undefined : moment(filter.agreementPeriod.from, "YYYY MM DD");
  const agreementPeriodTo =
    filter.agreementPeriod == null ? undefined : moment(filter.agreementPeriod.graceTo, "YYYY MM DD");

  const filesWithBookmarks = new Set(useEvidenceStore().evidence.map((evidence) => evidence.fileReference));

  const filteredFiles = requestedFiles.filter((file) => {
    if (filter.hideDrafts && String(file.isDraft).toLowerCase() === "true") return false;

    if (checkFolder && filter.groupByFolders && filter.currentFolder !== file.folder) return false;

    if (adviceInstanceFilters != null && !adviceInstanceFilters.has(file.fileReference)) return false;

    if (filter.agreementPeriod) {
      if (!file.edxIssueDate) return false;

      const issueDate = toMoment(file.edxIssueDate, true);
      if (issueDate?.isBetween(agreementPeriodFrom, agreementPeriodTo, undefined, "[]") !== true) return false;
    }

    if (!evidenceCountFilters.has("any")) {
      const evidenceCounts =
        file.evidenceCounts != null && file.evidenceCounts.length > 0 ? file.evidenceCounts : NO_INDICATORS;
      const matchesEvidenceCount = evidenceCounts.some((fileEvidenceCount) =>
        evidenceCountFilters.has(fileEvidenceCount.name.toLowerCase()),
      );

      if (!matchesEvidenceCount) return false;
    }

    if (
      !classificationFilters.has("Any") &&
      !classificationFilters.has(file.aiClassificationGroup || "Unknown") &&
      !classificationFilters.has(file.aiClassification || "Unknown")
    ) {
      return false;
    }

    if (!matchesSummariserStatus(file)) return false;

    if (!file.edxIssueDate) {
      if (!filter.dateRange.includeBlanks) return false;
    } else if (dateRangeFrom != null || dateRangeTo != null) {
      const dateParts = /^(?<year>\d{4,6})\s?(?<month>\d{2})?\s?(?<day>\d{2})?$/.exec(file.edxIssueDate);
      if (!dateParts) return false;

      const { year, month, day } = dateParts.groups!;

      // Get the issue date with the month and a set start date
      let issueDate = moment(
        `${year || dateRangeUpper.format("YYYY")} ${month || dateRangeUpper.format("MM")} ${day || dateRangeUpper.format("DD")}`,
        "YYYY MM DD",
      );
      // we have no day set, and the upperRange could trigger an nvalid date
      if (!issueDate.isValid()) {
        issueDate = moment(
          `${year || dateRangeUpper.format("YYYY")} ${month || dateRangeUpper.format("MM")} ${day || "01"}`,
          "YYYY MM DD",
        )
          .add(1, "month")
          .subtract(1, "day");
      }

      if (!issueDate.isBetween(dateRangeFrom ?? issueDate, dateRangeTo ?? issueDate, undefined, "[]")) return false;
    }

    if (tagFilters.size > 0) {
      const fileTagsByType = new Map<string, Set<string>>();
      for (const tag of file.allTags ?? []) {
        if (fileTagsByType.has(tag.tagType)) fileTagsByType.get(tag.tagType)!.add(tag.name);
        else fileTagsByType.set(tag.tagType, new Set([tag.name]));
      }

      for (const [tagType, tagFilter] of tagFilters) {
        const fileTags = fileTagsByType.get(tagType);
        if (fileTags == null) {
          if (!tagFilter.includeBlanks) return false;
          continue;
        }

        if (tagFilter.tags.has("Any")) continue;

        let matchesTag = false;

        for (const tag of fileTags) {
          if (tagFilter.tags.has(tag)) {
            matchesTag = true;
            break;
          }
        }

        if (!matchesTag) return false;
      }
    }

    return true;
  });

  function matchesSummariserStatus(file: Metadata): boolean {
    if (summariserStatusFilters.has("Any")) return true;

    if (summariserStatusFilters.has("Records with bookmarks") && filesWithBookmarks.has(file.fileReference))
      return true;
    if (
      summariserStatusFilters.has("Records not processed by Evidence AI") &&
      (!file.edxSummariserStatus || /not applicable|not required/i.test(file.edxSummariserStatus as string))
    )
      return true;
    if (
      summariserStatusFilters.has("Records processed by Evidence AI") &&
      /pending|no results|NoResults|ready|issue/i.test(file.edxSummariserStatus as string)
    )
      return true;
    if (
      summariserStatusFilters.has("Records with outstanding AI Evidence") &&
      annotationStore.annotationDictionary[file.fileReference]?.some((mark) =>
        /candidate/i.test(getMarkStatus(mark)),
      ) === true
    )
      return true;

    return false;
  }

  return Object.freeze(filteredFiles);
}
